/* ============================================
   LeadResponsable — de quién es esta oportunidad

   Va PRIMERO en la barra lateral, antes incluso de cuánto vale: cuando una
   oportunidad entra sola —del formulario de la web, del booking, del
   chatbot— nace sin dueño, y lo primero que hay que resolver no es el monto
   sino quién la agarra.

   Dos formas según quién mira:
   - Ventas ve un botón para tomarla, y sólo eso. Repartir la cartera es de
     quien la reparte; el backend rechaza con 403 cualquier intento de
     nombrar a un tercero, así que ofrecer un selector sería ofrecer un error.
   - Un admin ve el selector con todo el equipo.
   ============================================ */
import { useEffect, useState } from 'react'
import { useApi } from '@/hooks/useApi'
import { useAuth } from '@/hooks/useAuth'
import { showToast } from '@/components/layout/Toast'
import { IconUser } from '@/components/icons/Icons'
import type { Lead, MiembroEquipo } from '@/lib/types'

interface Props {
  lead: Lead
  onRefresh: () => void
}

export default function LeadResponsable({ lead, onRefresh }: Props) {
  const api = useApi()
  const { isAdmin, user } = useAuth()
  const [equipo, setEquipo] = useState<MiembroEquipo[]>([])
  const [guardando, setGuardando] = useState(false)

  useEffect(() => {
    if (!isAdmin) return
    api.get<MiembroEquipo[]>('/api/auth/equipo')
      .then(d => setEquipo(Array.isArray(d) ? d : []))
      .catch(() => { /* sin equipo el selector queda vacío: no rompe la ficha */ })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin])

  const nombreDe = (m: MiembroEquipo) => m.nombre || m.email
  const actual = equipo.find(m => m.id === lead.responsable_id)
  const esMia = lead.responsable_id != null && lead.responsable_id === user?.id
  const sinDueno = lead.responsable_id == null

  async function asignar(valor: number | null, mensaje: string) {
    setGuardando(true)
    try {
      await api.put(`/api/leads/${lead.id}`, { responsable_id: valor })
      onRefresh()
      showToast(mensaje, 'success')
    } catch (err: any) {
      showToast(err?.message || 'No se pudo cambiar el responsable', 'error')
    } finally {
      setGuardando(false)
    }
  }

  return (
    <section className="ld-panel">
      <div className="ld-panel__header">
        <h4 className="ld-panel__titulo"><IconUser size={14} /> Responsable</h4>
      </div>

      {isAdmin ? (
        <select
          className="form-select form-select-sm"
          value={lead.responsable_id ?? ''}
          disabled={guardando}
          onChange={e => {
            const v = e.target.value ? Number(e.target.value) : null
            const quien = equipo.find(m => m.id === v)
            asignar(v, quien ? `Asignada a ${nombreDe(quien)}` : 'Quedó sin responsable')
          }}
          aria-label="Responsable de la oportunidad"
        >
          <option value="">Sin asignar</option>
          {equipo.map(m => <option key={m.id} value={m.id}>{nombreDe(m)}</option>)}
        </select>
      ) : sinDueno ? (
        <button
          className="btn btn-primary btn-sm"
          disabled={guardando}
          onClick={() => asignar(user?.id ?? null, 'La tomaste')}
        >
          {guardando ? 'Tomando...' : 'Tomar esta oportunidad'}
        </button>
      ) : (
        <span className="ld-dato__valor">
          {esMia ? 'Tuya' : (actual ? nombreDe(actual) : 'Asignada')}
        </span>
      )}

      {/* Soltarla la devuelve a la bandeja. Sólo tiene sentido ofrecerlo sobre
          la propia: el backend rechaza tocar la de otro. */}
      {!isAdmin && esMia && (
        <button
          className="btn btn-secondary btn-sm"
          disabled={guardando}
          onClick={() => asignar(null, 'La soltaste: vuelve a la bandeja')}
        >
          Soltarla
        </button>
      )}
    </section>
  )
}
